/**
 * The floor every preset stands on: the shape of one, and the parts they share.
 *
 * Split out of the old single `presets.ts` when a second family of letters
 * arrived. The letters themselves are copy, and copy grows; this file is the
 * contract, and it should not. Keeping them apart means adding a campaign never
 * touches the file that says what a preset IS.
 *
 * Both languages are written out in the letter files rather than pulled from
 * `messages/*.json`. A preset body is CONTENT that gets copied into a letter and
 * then edited, not interface text: it has to sit next to the block that carries
 * it, and it must not change under an operator's feet when an interface string
 * is retranslated. A campaign is one language (that is the model's rule, not
 * this file's), so the campaign's own locale picks the variant.
 */
import { DEFAULT_SIGNATURE } from "../signatures";
import {
  DEFAULT_BLOCK_PROPS,
  DEFAULT_PAGE,
  DESIGN_VERSION,
  newBlockId,
  type Block,
  type BlockType,
  type EmailDesign,
  type PageStyle,
} from "../types";

export type PresetLocale = "et" | "en";

/** A string that exists in both languages. */
export type L = Record<PresetLocale, string>;

export const pick = (v: L, locale: PresetLocale): string => v[locale] ?? v.et;

export interface Preset {
  id: string;
  /** i18n suffix: `marketing.preset.<key>.name` / `.description`. */
  key: string;
  tone: "plain" | "report" | "offer" | "followup";
  subject: L;
  build: (locale: PresetLocale) => EmailDesign;
}

export function b(type: BlockType, props: Record<string, unknown> = {}): Block {
  return { id: newBlockId(), type, props: { ...DEFAULT_BLOCK_PROPS[type], ...props } };
}

export function design(blocks: Block[], page: Partial<PageStyle> = {}): EmailDesign {
  return { version: DESIGN_VERSION, page: { ...DEFAULT_PAGE, ...page }, blocks };
}

/** A plain-looking letter: white on white, square corners, no card shadow. The
 *  page settings are as much of the "this is not a newsletter" effect as the
 *  block choice is. */
export const PLAIN_PAGE: Partial<PageStyle> = {
  cardColor: "#ffffff",
  backgroundColor: "#ffffff",
  radius: 0,
  paddingX: 24,
};

/** The unsubscribe line, worded once. Every preset gets the same one so the
 *  promise does not drift between them. */
const FOOTER_TEXT: L = {
  et: "Kirjutame, sest {{company}} on Eesti äriregistris ja tema koduleht on avalikult nähtav.",
  en: "We are writing because {{company}} is in the Estonian business register and its website is publicly visible.",
};
const FOOTER_OPT_OUT: L = { et: "Ma ei soovi rohkem kirju", en: "I would rather not hear from you again" };

// No address line. The wire adds the legal identification itself now - name,
// registry code and street, on every letter and in the campaign's language
// (api `crm-marketing/lib/legal/identification.ts`). "WebAdmin OU, Tallinn"
// used to sit here, and it was the worse of the two: it looks like the
// identification a business letter owes, it is not one, and a preset the
// operator can edit is the wrong home for a line a law requires.
export const footer = (locale: PresetLocale): Block =>
  b("footer", {
    text: pick(FOOTER_TEXT, locale),
    unsubscribeText: pick(FOOTER_OPT_OUT, locale),
    address: "",
  });

/**
 * Who signs, read from the one list the picker in the inspector also reads.
 *
 * WebAdmin OU rather than Husly, and that is the point of the default: these
 * letters go out from the agency, the legal identification the wire attaches
 * names the agency, and a signature naming the product would be the one line
 * in the letter claiming somebody else wrote it. Signing as Husly is a variant
 * an operator picks, not a thing a preset decides for them.
 */
export const signature = (): Block => b("signature", { ...DEFAULT_SIGNATURE.fields });
